import React, { useRef, useState } from "react";
import { withTheme } from "@material-ui/core/styles";
import styled from "styled-components";
import { NavLink, useHistory } from "react-router-dom";
import {
  logo,
  bellIcon,
  caret,
  homeIcon,
  courseIcon,
  marketplaceIcon,
} from "../../assets/svgs";
import Avatar from "../common/Avatar";
import { useClickOutside } from "../../hooks";
import { useAuthContext } from "../../contexts/authContext";
import { useLearningSpaceContext } from "../../contexts/learningSpaceContext";

const DesktopNavigation: React.FC<any> = () => {
  const { isAuthenticated, logout, userInfo }: any = useAuthContext();
  const learningSpaceContext: any = useLearningSpaceContext();
  // Only known once the list has loaded; the badge stays hidden until then.
  const spaceCount = learningSpaceContext?.data?.learningSpaces?.total;
  const history = useHistory();
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);

  useClickOutside(userMenuRef, () => setIsUserMenuOpen(false));

  return (
    <NavContentContainer>
      <div>
        <img src={logo} alt="logo" />
      </div>
      <LinksContainer>
        <Links to="/" exact>
          <img src={homeIcon} alt="home-icon" />
          Learning Spaces
          {typeof spaceCount === "number" ? (
            <CountBadge>{spaceCount}</CountBadge>
          ) : null}
        </Links>
        {/* <Links to="/courses" exact>
          <img src={courseIcon} alt="course-icon" />
          Courses
        </Links> */}
        {/* <Links to="/marketplace" exact>
          <img src={marketplaceIcon} alt="marketplace-icon" />
          Marketplace
        </Links> */}
      </LinksContainer>
      <RightNavContainer>
        <Container>
          <img src={bellIcon} alt="notification-bell" />
        </Container>
        {isAuthenticated ? (
          <UserMenuContainer ref={userMenuRef}>
            <UserMenuTrigger
              type="button"
              aria-haspopup="true"
              aria-expanded={isUserMenuOpen}
              onClick={() => setIsUserMenuOpen((open) => !open)}
            >
              <Container>
                <Avatar
                  url={null}
                  name={userInfo?.firstname || userInfo?.email || "?"}
                />
              </Container>
              <Container>
                <Caret src={caret} alt="caret" $isOpen={isUserMenuOpen} />
              </Container>
            </UserMenuTrigger>
            {isUserMenuOpen && (
              <UserMenu>
                <UserMenuHeader>
                  <strong>
                    {[userInfo?.firstname, userInfo?.lastname]
                      .filter(Boolean)
                      .join(" ") || "Signed in"}
                  </strong>
                  <span>{userInfo?.email}</span>
                </UserMenuHeader>
                <MenuAction
                  type="button"
                  onClick={() => {
                    setIsUserMenuOpen(false);
                    history.push("/profile");
                  }}
                >
                  Profile
                </MenuAction>
                <LogoutButton type="button" onClick={logout}>
                  Log out
                </LogoutButton>
              </UserMenu>
            )}
          </UserMenuContainer>
        ) : null}
      </RightNavContainer>
    </NavContentContainer>
  );
};
const activeClassName = "nav-item-active";
const NavContentContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;
const RightNavContainer = styled.div`
  display: flex;
  align-items: center;
`;
const Links = withTheme(styled(NavLink).attrs({ activeClassName })`
  &.${activeClassName} {
    color: ${(props) => props.theme.colors.white};
    font-weight: ${(props) => props.theme.fontWeights.bold};
  }
  &:hover{
    background: rgba(5, 18, 29, 0.33);
  }
  display:flex;
  align-items:center
  font-weight: ${(props) => props.theme.fontWeights.normal};
  transition: all 0.3s;
  text-decoration: none;
  color: ${(props) => props.theme.colors.white};
  margin: 0 30px;
  padding: 6px 12px;
  border-radius: ${(props) => props.theme.radius.normal}px;
  & img {
    margin-right: 8px;
  }
`);
const CountBadge = withTheme(styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-left: 10px;
  background: rgba(255, 255, 255, 0.22);
  border-radius: 20px;
  padding: 0 10px;
  min-width: 28px;
  height: 26px;
  font-size: 15px;
  font-weight: ${(props) => props.theme.fontWeights.bold};
  line-height: 1;
`);
const LinksContainer = styled.div`
  display: flex;
`;
const Container = styled.div`
  padding: 0 10px;
`;
const UserMenuContainer = styled.div`
  position: relative;
`;
const UserMenuTrigger = withTheme(styled.button`
  display: flex;
  align-items: center;
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 4px;
  border-radius: ${(props) => props.theme.radius.normal}px;
  transition: all 0.3s;
  &:hover {
    background: rgba(5, 18, 29, 0.33);
  }
`);
const Caret = styled.img<{ $isOpen: boolean }>`
  transition: transform 0.3s;
  transform: ${(props) => (props.$isOpen ? "rotate(180deg)" : "none")};
`;
const UserMenu = withTheme(styled.div`
  position: absolute;
  top: calc(100% + 12px);
  right: 0;
  min-width: 160px;
  z-index: 10;
  background: ${(props) => props.theme.colors.white};
  border-radius: ${(props) => props.theme.radius.small}px;
  box-shadow: 0px 4px 16px rgba(12, 37, 57, 0.2);
  padding: 8px 0;
`);
const UserMenuHeader = withTheme(styled.div`
  padding: 6px 20px 12px 20px;
  border-bottom: 1px solid ${(props) => props.theme.colors.gray200};
  margin-bottom: 6px;

  & strong {
    display: block;
    font-size: 14px;
    color: ${(props) => props.theme.colors.black};
  }
  & span {
    display: block;
    font-size: 12px;
    color: ${(props) => props.theme.colors.gray400};
    overflow: hidden;
    text-overflow: ellipsis;
  }
`);
const MenuAction = withTheme(styled.button`
  display: block;
  width: 100%;
  background: transparent;
  border: none;
  cursor: pointer;
  text-align: left;
  padding: 10px 20px;
  font-size: 14px;
  color: ${(props) => props.theme.colors.black};
  &:hover {
    background: ${(props) => props.theme.colors.gray100};
  }
`);
const LogoutButton = withTheme(styled.button`
  display: block;
  width: 100%;
  background: transparent;
  border: none;
  cursor: pointer;
  text-align: left;
  padding: 10px 20px;
  font-size: 14px;
  font-weight: ${(props) => props.theme.fontWeights.medium};
  color: ${(props) => props.theme.colors.red};
  &:hover {
    background: ${(props) => props.theme.colors.gray100};
  }
`);

export default DesktopNavigation;
